import { ArrowBack } from '@mui/icons-material';
import { Alert, CircularProgress } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ROUTE_REDESIGN } from 'js/constants/pathnames';
import * as PATHS from 'new/constants/pathnames';
import { ScreenComponent, ScreenConfig } from 'new/routes/RouterManager';

import { HelmCommandPreview } from 'new/modules/settings/capsules/components/HelmCommandPreview/HelmCommandPreview';
import {
  CapsuleDetailsSection,
  DangerZone,
  DeleteDialog,
  KeysSection,
} from 'new/modules/settings/capsules/screens/CapsuleDetailScreen/components';
import { useCapsuleDetail } from 'new/modules/settings/capsules/screens/CapsuleDetailScreen/hooks';
import { formatTableRegistered } from 'new/modules/settings/capsules/domain';
import styles from 'new/modules/settings/capsules/screens/CapsuleDetailScreen/CapsuleDetailScreen.module.scss';

export const CapsuleDetailScreen: ScreenComponent<Record<string, never>> = () => {
  const {
    backHref,
    capsuleId,
    dto,
    detail,
    showLoading,
    showError,
    fetchErrorMessage,
    deleteOpen,
    setDeleteOpen,
    actionError,
    clearActionError,
    handleDelete,
    isDeleting,
  } = useCapsuleDetail();

  if (!capsuleId) {
    return (
      <div className={styles.screenRoot}>
        <RouterLink to={backHref} className={styles.backLink}>
          <ArrowBack fontSize="small" />
          Back to Capsules
        </RouterLink>
        <span className={styles.errorMessage}>Missing capsule id.</span>
      </div>
    );
  }

  if (showLoading) {
    return (
      <div className={styles.centered} data-testid="capsule-detail-loading">
        <CircularProgress size={32} />
      </div>
    );
  }

  if (showError) {
    return (
      <div className={styles.screenRoot}>
        <RouterLink to={backHref} className={styles.backLink}>
          <ArrowBack fontSize="small" />
          Back to Capsules
        </RouterLink>
        <span className={styles.errorMessage}>{fetchErrorMessage}</span>
      </div>
    );
  }

  if (detail == null || dto == null) return null;

  const generatedKeys = {
    apiAccessKey: dto.api_access_key ?? '',
    registrationKey: dto.registration_key ?? '',
  };
  const hasMaskedHelmKeys = Object.values(generatedKeys).some((key) => key.includes('*****'));

  return (
    <div className={styles.screenRoot}>
      <RouterLink to={backHref} className={styles.backLink}>
        <ArrowBack fontSize="small" />
        Back to Capsules
      </RouterLink>

      <div className={styles.titleRow}>
        <h1 className={styles.title}>{detail.title}</h1>
      </div>

      <CapsuleDetailsSection
        capsuleId={dto.id ? String(dto.id) : undefined}
        uid={dto.uid}
        orgId={dto.org_id != null ? String(dto.org_id) : undefined}
        createdAt={dto.created_at ? formatTableRegistered(dto.created_at) : undefined}
        description={detail.description}
      />

      <KeysSection capsule={dto} />

      <div className={styles.helmPreviewSection}>
        <HelmCommandPreview
          generatedKeys={generatedKeys}
          dataplaneUid={dto.uid}
          orgId={dto.org_id}
          capsuleName={dto.name}
        />

        {hasMaskedHelmKeys ? (
          <Alert severity="warning" variant="outlined" className={styles.maskedKeysAlert}>
            This command uses the masked API Access Key and Encryption/Decryption Key returned by
            the API. Copying the command preserves those masked values.
          </Alert>
        ) : null}
      </div>

      <DangerZone
        actionError={actionError}
        isDeleting={isDeleting}
        onDelete={() => setDeleteOpen(true)}
        onClearError={clearActionError}
      />

      <DeleteDialog
        open={deleteOpen}
        isLoading={isDeleting}
        capsuleTitle={detail.title}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDelete}
      />
    </div>
  );
};

CapsuleDetailScreen.screenConfig = {
  path: `${ROUTE_REDESIGN}${PATHS.ROUTE_SETTINGS_CAPSULE_DETAIL}`,
  exact: true,
  screenName: 'CapsuleDetail',
  domain: 'settings',
  pageTitle: 'Capsule',
  header: {
    title: 'Capsule',
    slugTitle: 'Capsule',
  },
} as ScreenConfig<Record<string, never>>;
